import { Injectable } from '@angular/core';
import { Observable, ConnectableObservable, Subscription } from 'rxjs';
import { map, publishLast, publishReplay } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { CultureInfo } from './culture-info';
import { deserializeArray } from 'santee-dcts';
import { Settings } from './settings';
import { FileLink } from '../documents';

export type LangSorter = (ci: FileLink) => number;

@Injectable()
export class LanguagesStore {
    private languageOrderer: ConnectableObservable<LangSorter> = null;
    private languages: ConnectableObservable<CultureInfo[]> = null;
    private allLanguageSubscription: Subscription = null;
    private orderLanguageSubscription: Subscription = null;

    constructor(private http: HttpClient, private settings: Settings) { }

    public dispose() {
        this.allLanguageSubscription && this.allLanguageSubscription.unsubscribe();
        this.orderLanguageSubscription && this.orderLanguageSubscription.unsubscribe();
    }

    public get allLanguages() {
        if (this.languages === null) {
            this.languages = this.getLanguages();
            this.allLanguageSubscription = this.languages.connect();
        }

        return this.languages;
    }

    public get orderLanguage(): Observable<LangSorter> {
        if (this.languageOrderer === null) {
            this.languageOrderer = this.allLanguages
                .pipe(
                    map(langs => {
                        const languageIndexes: { [langId: string]: number } = {};
                        langs.forEach((l, index) => languageIndexes[l.languageRowId] = index);
                        const sortFunc = (lang: FileLink) =>
                            languageIndexes[lang.languageId];
                        return sortFunc;
                    }),
                    publishReplay(1)
                ) as ConnectableObservable<LangSorter>;
            this.orderLanguageSubscription = this.languageOrderer.connect();
        }
        return this.languageOrderer;
    }

    private getLanguages(): ConnectableObservable<CultureInfo[]> {
        return this.http
            .get<Object[]>(`${this.settings.apiUrl}/user/languages`)
            .pipe(
                map((rawData) => deserializeArray(rawData, CultureInfo)),
                publishLast()) as ConnectableObservable<CultureInfo[]>;
    }
}